import {
  companyName,
  policySections,
  readPolicyContent,
  resolvePolicyCheckboxCopy,
  sanitizeHtml,
} from "@1-now/booking-core";
import { getPrivacyPolicyContent, getRentalAgreementContent } from "api/rental-agreement/rental-agreement";
import { getAgreementPdfBase } from "utils/agreement-url";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Checkbox, Dialog, DialogPanel, Transition } from "@headlessui/react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import toast from "react-hot-toast";
import PrivacyPolicy from "designs/Common/CheckOutPage/PrivacyPolicy";
import TermsandConditions from "designs/Common/CheckOutPage/TermsandConditions";
import Cancellation from "designs/Common/CheckOutPage/Cancellation";
import BackRouteButton from "shared/BackRouteButton/BackRouteButton";
import { useData } from "data/data-provider";
import { trackBookingStep, trackBookingStepError } from "analytics/bookingEvents";

interface PayLaterPoliciesState {
  bookingIdentifier: string;
  pdfUrl: string;
  agreementContent: any;
  privacyPolicyContent: any;
  cancellationPolicyContent: any;
}

/**
 * What the accept step below opens with for a pay-later booking just made: its filled-in agreement and that
 * agreement's own policy content. The car pages open it with this, and so does the Reserved page once a returning
 * renter's email code is right (1NOW-10770), so both accept the same booking's agreement the same way.
 */
export const payLaterPoliciesState = async (bookingIdentifier: string): Promise<PayLaterPoliciesState> => {
  /* The agreement key is the booking's uuid. The old
     `{pk}b6cr4{companyId}b6cr4{base64(email)}` salt read like a secret
     but was not — the backend only ever looked at the first segment, so
     a guessed integer opened any operator's agreement (1NOW-10707). */
  const state: PayLaterPoliciesState = {
    bookingIdentifier,
    pdfUrl: `${getAgreementPdfBase()}${bookingIdentifier}`,
    agreementContent: null,
    privacyPolicyContent: [],
    cancellationPolicyContent: [],
  };
  try {
    const agResponse = await getRentalAgreementContent(bookingIdentifier);
    if (agResponse?.data) {
      state.agreementContent = agResponse.data;
      state.privacyPolicyContent = agResponse.data.content.privacy_policy;
      state.cancellationPolicyContent = agResponse.data.content.cancellation_policy;
    }
  } catch (e) {
    console.error("Error fetching rental agreement:", e);
  }
  return state;
};

/**
 * 1NOW-11061: "Pay Later Policy" is also a menu chip in Website Settings, so
 * this URL gets opened cold from the header: no booking, so none of the router
 * state the accept step below is fed by. It used to render that step anyway —
 * an empty Rental Agreement frame and a Terms modal that threw into the error
 * boundary. Without a booking it reads the company's own policies, the same
 * public copy /terms-and-conditions shows.
 */
const PayLaterPolicyInfo: FC = () => {
  const { ourCompany, companyDetails } = useData();
  // undefined = still loading, null = the request failed.
  const [policies, setPolicies] = useState<Record<string, any[]> | null>();

  useEffect(() => {
    let live = true;
    getPrivacyPolicyContent().then((r) => live && setPolicies(readPolicyContent(r)));
    return () => {
      live = false;
    };
  }, []);

  const terms = policySections(policies ?? null, "terms_condition");
  const privacy = policySections(policies ?? null, "privacy_policy");
  const cancellation = policySections(policies ?? null, "cancellation_policy");
  const published = terms.length + privacy.length + cancellation.length > 0;

  return (
    <div className="container mt-11 pb-10">
      <div className="max-w-2xl mx-auto">
        <div className="mt-2 mb-6">
          <BackRouteButton />
        </div>
        <div className="w-full flex flex-col sm:rounded-md sm:border border-secondary-100 space-y-4 px-0 sm:p-6 xl:p-8">
          <h1 className="text-2xl font-bold text-text-color">Pay Later Policy</h1>
          <p className="text-sm text-text-color">
            With Pay Later nothing is charged online. When you reserve a car you review and accept
            the Rental Agreement (made out for your booking), the Terms and Conditions, and the
            Privacy and Cancellation Policies below.
          </p>
          {policies === undefined ? (
            <div className="flex min-h-[20vh] items-center justify-center">
              <div className="spinner-border inline-block h-6 w-6 animate-spin rounded-full border-4 border-t-transparent border-secondary-100"></div>
            </div>
          ) : policies === null ? (
            <p className="text-sm text-text-color">
              We couldn't load these policies. Please try again in a moment.
            </p>
          ) : !published ? (
            <p className="text-sm text-text-color">These policies have not been published yet.</p>
          ) : (
            <>
              {terms.length > 0 && (
                <TermsandConditions
                  data={{
                    company: { name: companyName(companyDetails, ourCompany) },
                    content: { terms_condition: terms },
                  }}
                />
              )}
              {privacy.length > 0 && <PrivacyPolicy terms={privacy} />}
              {cancellation.length > 0 && <Cancellation terms={cancellation} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PayLaterAcceptPolicies: FC = () => {
  const { companyDetails, bookingdetails } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as PayLaterPoliciesState;
  const {
    bookingIdentifier,
    pdfUrl,
    agreementContent,
    privacyPolicyContent,
    cancellationPolicyContent,
  } = state;

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rentalAgreementAccepted, setRentalAgreementAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [isTermOpen, setIsTermOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isCancellationOpen, setIsCancellationOpen] = useState(false);
  const [isRentalAgreementPdfOpen, setIsRentalAgreementPdfOpen] = useState(false);
  const policyCheckboxCopy = useMemo(
    () => resolvePolicyCheckboxCopy(companyDetails),
    [companyDetails],
  );

  // 1NOW-11501: the pay-later policies are a step of the funnel (the booking's number, never its uuid, as at 1NOW-10869).
  useEffect(() => {
    trackBookingStep("pay_later_policies", {
      pay_mode: "later",
      booking_id: bookingdetails?.unique_identifier === bookingIdentifier ? bookingdetails?.id : undefined,
    });
  }, []);

  const refuse = (message: string) => {
    toast.error(message);
    trackBookingStepError("pay_later_policies", message);
  };
  const handleProceed = () => {
    if (!rentalAgreementAccepted) {
      refuse("Please review and accept the Rental Agreement");
      return;
    }
    if (!termsAccepted) {
      refuse("Please review and accept the Terms and Conditions");
      return;
    }
    if (!privacyPolicyAccepted) {
      refuse("Please review and accept the Privacy & Cancellation Policies");
      return;
    }
    toast.success("Your Booking is reserved");
    // 1NOW-11476 (Taha): with the policies accepted, a pay-later booking opens on its booking page, restricted until it
    // is paid (details, invoice and agreement, and Pay and book; no ID / insurance, edit, cancel or other bookings).
    // Daily and long-term alike; the booking page lists a long-term stay's payments.
    const booking = bookingdetails?.unique_identifier === bookingIdentifier ? bookingdetails : undefined;
    navigate(`/booking-details?id=${encodeURIComponent(bookingIdentifier)}`, { state: { booking } });
  };

  const allAccepted = rentalAgreementAccepted && termsAccepted && privacyPolicyAccepted;

  const toggleAcceptAll = (checked: boolean) => {
    setRentalAgreementAccepted(checked);
    setTermsAccepted(checked);
    setPrivacyPolicyAccepted(checked);
    if (checked) toast.success("All policies accepted");
  };

  return (
    <>
      <div className="container mt-11 pb-10">
        <div className="max-w-2xl mx-auto">
          <div className="mt-2 mb-6">
            <BackRouteButton />
          </div>

          <div className="w-full flex flex-col sm:rounded-md sm:border border-secondary-100 space-y-4 px-0 sm:p-6 xl:p-8">
            <h2 className="text-2xl font-bold text-text-color">
              Review &amp; Accept Policies
            </h2>
            <p className="text-sm text-text-color">
              Please review and accept all policies before proceeding to your booking.
            </p>

            {/* Rental Agreement */}
            <div className="flex gap-2 p-4 border border-secondary-100 rounded-md overflow-hidden">
              <Checkbox
                checked={rentalAgreementAccepted}
                onChange={(checked) => setRentalAgreementAccepted(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20 ring-inset mr-4 flex-shrink-0"
              >
                <CheckIcon
                  className={`size-5 stroke-primary-brand ${rentalAgreementAccepted ? "visible" : "invisible"}`}
                />
              </Checkbox>
              <div>
                <div
                  className="policy-checkbox-copy text-text-color text-lg font-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(policyCheckboxCopy.rentalAgreementText,) }}
                />
                <div
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsRentalAgreementPdfOpen(true)}
                >
                  Rental Agreement
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="flex gap-2 p-4 border border-secondary-100 rounded-md overflow-hidden">
              <Checkbox
                checked={termsAccepted}
                onChange={(checked) => setTermsAccepted(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20 ring-inset mr-4 flex-shrink-0"
              >
                <CheckIcon
                  className={`size-5 stroke-primary-brand ${termsAccepted ? "visible" : "invisible"}`}
                />
              </Checkbox>
              <div>
                <div
                  className="policy-checkbox-copy text-text-color text-lg font-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(policyCheckboxCopy.termsConditionsText,) }}
                />
                <div
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsTermOpen(true)}
                >
                  Terms and Conditions
                </div>
              </div>
            </div>

            {/* Privacy Policy & Cancellation Policy */}
            <div className="flex gap-2 p-4 border border-secondary-100 rounded-md overflow-hidden">
              <Checkbox
                checked={privacyPolicyAccepted}
                onChange={(checked) => setPrivacyPolicyAccepted(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20 ring-inset mr-4 flex-shrink-0"
              >
                <CheckIcon
                  className={`size-5 stroke-primary-brand ${privacyPolicyAccepted ? "visible" : "invisible"}`}
                />
              </Checkbox>
              <div>
                <div
                  className="policy-checkbox-copy text-text-color text-lg font-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(policyCheckboxCopy.privacyCancellationText,) }}
                />
                <span
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsPrivacyOpen(true)}
                >
                  Privacy Policy
                </span>
                <span className="text-text-color text-xl font-extrabold mx-2">
                  &amp;
                </span>
                <span
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsCancellationOpen(true)}
                >
                  Cancellation Policy
                </span>
              </div>
            </div>

            <div className="flex gap-2 mt-6 p-4 border border-secondary-100 rounded-md overflow-hidden items-center">
              <Checkbox
                checked={allAccepted}
                onChange={(checked) => toggleAcceptAll(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20 ring-inset mr-2"
              >
                <CheckIcon className={`size-5 stroke-primary-brand ${allAccepted ? "visible" : "invisible"}`} />
              </Checkbox>
              <div className="text-text-color "><p className="font-bold">Accept all policies</p>  <p className="text-text-color text-sm">
                By proceeding you agree to the{' '}
                <button type="button" onClick={() => setIsRentalAgreementPdfOpen(true)} className="underline text-text-color">
                  Rental Agreement
                </button>
                ,{' '}
                <button type="button" onClick={() => setIsTermOpen(true)} className="underline text-text-color">
                  Terms and Conditions
                </button>
                ,{' '}
                <button type="button" onClick={() => setIsPrivacyOpen(true)} className="underline text-text-color">
                  Privacy Policies
                </button>
                , and the{' '}
                <button type="button" onClick={() => setIsCancellationOpen(true)} className="underline text-text-color">
                  Cancellation Policies
                </button>
                .
              </p></div>
            </div>

            <ButtonPrimary className="w-full rounded-md" onClick={handleProceed}>
              Proceed to Booking
            </ButtonPrimary>
          </div>
        </div>
      </div>

      {/* Terms & Conditions sub-modal */}
      <Transition appear show={isTermOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsTermOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isTermOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsTermOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                        <TermsandConditions data={agreementContent} />
                      </div>
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-secondary-100 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setTermsAccepted(true);
                        setIsTermOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Privacy Policy sub-modal */}
      <Transition appear show={isPrivacyOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsPrivacyOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isPrivacyOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsPrivacyOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                        <PrivacyPolicy terms={privacyPolicyContent} />
                      </div>
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-secondary-100 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setPrivacyPolicyAccepted(true);
                        setIsPrivacyOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Cancellation Policy sub-modal */}
      <Transition appear show={isCancellationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsCancellationOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isCancellationOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4 z-0">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsCancellationOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                        <Cancellation terms={cancellationPolicyContent} />
                      </div>
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-secondary-100 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setPrivacyPolicyAccepted(true);
                        setIsCancellationOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Rental Agreement PDF sub-modal */}
      <Transition appear show={isRentalAgreementPdfOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsRentalAgreementPdfOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isRentalAgreementPdfOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsRentalAgreementPdfOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <iframe
                        src={pdfUrl}
                        title="Rental Agreement"
                        className="w-full h-full"
                        style={{ minHeight: "60vh" }}
                      />
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-secondary-100 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setRentalAgreementAccepted(true);
                        setIsRentalAgreementPdfOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

const PayLaterPoliciesPage: FC = () => {
  const { bookingIdentifier } = (useLocation().state ?? {}) as Partial<PayLaterPoliciesState>;
  return bookingIdentifier ? <PayLaterAcceptPolicies /> : <PayLaterPolicyInfo />;
};

export default PayLaterPoliciesPage;
